import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';

function Stars({ rating, size = 14 }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {[1,2,3,4,5].map(i => (
        <svg key={i} width={size} height={size} viewBox="0 0 20 20"
          fill={i <= Math.round(rating) ? tokens.color.amber500 : "#d1d5db"}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      ))}
    </span>
  );
}

const TABS = ["About", "Client feedback", "Work history", "Portfolio", "Skills"];

export default function ProDrawer({ pro, onClose }) {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("About");
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const { isLoggedIn } = useAuth();

  const initials = pro?.name?.split(" ").map(w => w[0]).join("").slice(0, 2) || "?";
  const colors = [tokens.color.primary, "#0284c7","#7c3aed","#dc2626","#d97706","#0891b2"];
  const bg = pro ? colors[pro.name.charCodeAt(0) % colors.length] : tokens.color.primary;

  // Lock body scroll and hide navbar when drawer open
  useEffect(() => {
    if (pro) {
      document.body.style.overflow = "hidden";
      // Hide all navbars on the page
      document.querySelectorAll("nav, header").forEach(el => {
        el.dataset.drawerHidden = el.style.display;
        el.style.display = "none";
      });
      setActiveTab("About");
      requestAnimationFrame(() => setVisible(true));
    } else {
      document.body.style.overflow = "";
      // Restore navbars
      document.querySelectorAll("nav, header").forEach(el => {
        el.style.display = el.dataset.drawerHidden || "";
        delete el.dataset.drawerHidden;
      });
      setVisible(false);
    }
    return () => {
      document.body.style.overflow = "";
      document.querySelectorAll("nav, header").forEach(el => {
        el.style.display = el.dataset.drawerHidden || "";
        delete el.dataset.drawerHidden;
      });
    };
  }, [pro]);

  // Track active tab based on scroll position inside drawer
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const handleScroll = () => {
      let current = "About";
      TABS.forEach(tab => {
        const ref = sectionRefs.current[tab];
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const containerRect = el.getBoundingClientRect();
          if (rect.top - containerRect.top <= 80) current = tab;
        }
      });
      setActiveTab(current);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [visible]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const scrollToSection = (tab) => {
    setActiveTab(tab);
    const ref = sectionRefs.current[tab];
    const el = contentRef.current;
    if (ref && el) {
      const offset = ref.offsetTop - 16;
      el.scrollTo({ top: offset, behavior: "smooth" });
    }
  };

  if (!pro) return null;

  const handleBookNow = () => {
    if (!isLoggedIn) {
      // If not logged in, close drawer and redirect to login
      handleClose();
      setTimeout(() => navigate('/login'), 300);
    } else {
      // If logged in, proceed to booking
      handleClose();
      setTimeout(() => navigate(`/bookings?pro=${pro.id}`), 300);
    }
  };

  const handleMessage = () => {
    if (!isLoggedIn) {
      handleClose();
      setTimeout(() => navigate('/login'), 300);
    } else {
      handleClose();
      setTimeout(() => navigate(`/messages?pro=${pro.id}`), 300);
    }
  };

  const handleViewProfile = () => {
    handleClose();
    setTimeout(() => navigate(`/pro/${pro.id}`), 300);
  };

  const handleSimilarProClick = (skill) => {
    handleClose();
    setTimeout(() => navigate(`/search?skill=${encodeURIComponent(skill)}`), 300);
  };

  const drawerWidth = isMobile ? "100%" : (isTablet ? "95vw" : "min(1100px, 88vw)");
  const leftSidebarWidth = isMobile ? "100%" : 280;
  const leftSidebarPadding = isMobile ? "24px 20px 32px" : "28px 24px 40px";
  const avatarSize = isMobile ? 80 : 96;
  const nameFontSize = isMobile ? 18 : 20;

  return (
    <>
      {/* Backdrop */}
      <div onClick={handleClose} style={{
        position: "fixed", inset: 0, zIndex: 900,
        background: tokens.color.overlayDark,
        opacity: visible ? 1 : 0,
        transition: `opacity ${tokens.motion.medium} ${tokens.motion.ease}`,
        cursor: "pointer",
      }} />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 901,
        width: drawerWidth,
        background: tokens.color.white,
        boxShadow: "-4px 0 40px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
        transform: visible ? "translateX(0)" : "translateX(100%)",
        transition: `transform ${tokens.motion.medium} cubic-bezier(0.4,0,0.2,1)`,
        fontFamily: tokens.font.family.sans,
        overflow: "hidden",
      }}>

        {/* ── TOP BAR ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: isMobile ? "8px 16px" : "10px 24px",
          borderBottom: `1px solid ${tokens.color.neutral200}`,
          flexShrink: 0, background: tokens.color.white, zIndex: 10,
        }}>
          <button onClick={handleClose}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 14, color: tokens.color.neutral700, padding: 0, fontFamily: tokens.font.family.sans, minHeight: isMobile ? tokens.spacing[44] : "auto" }}
            onMouseEnter={e => e.currentTarget.style.color = "#111827"}
            onMouseLeave={e => e.currentTarget.style.color = tokens.color.neutral700}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
          <button
            onClick={handleViewProfile}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: tokens.color.accent, padding: 0, fontFamily: tokens.font.family.sans, minHeight: isMobile ? tokens.spacing[44] : "auto" }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
            onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
            Open profile in a new window
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/>
            </svg>
          </button>
        </div>

        {/* ── BODY: side-by-side, each scrolls independently ── */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden", flexDirection: isMobile ? "column" : "row" }}>

          {/* LEFT sidebar — its own scroll */}
          <div style={{
            width: isMobile ? "100%" : leftSidebarWidth,
            flexShrink: 0,
            borderRight: isMobile ? "none" : `1px solid ${tokens.color.neutral200}`,
            borderBottom: isMobile ? `1px solid ${tokens.color.neutral200}` : "none",
            overflowY: "auto",
            padding: leftSidebarPadding,
          }}>
            {/* Avatar */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ position: "relative", display: "inline-block" }}>
                <div style={{
                  width: avatarSize, height: avatarSize, borderRadius: "50%",
                  background: bg, overflow: "hidden",
                  border: `3px solid ${tokens.color.white}`,
                  boxShadow: `0 2px 12px ${tokens.color.shadowMd}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: isMobile ? 22 : 26, fontWeight: 800, color: tokens.color.white, margin: "0 auto",
                }}>
                  <img src={pro.image} alt={pro.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={e => e.target.style.display = "none"}
                  />
                </div>
                <div style={{ position: "absolute", bottom: 4, right: 4, width: 14, height: 14, borderRadius: "50%", background: pro.available ? tokens.color.accent : "#9ca3af", border: `2.5px solid ${tokens.color.white}` }} />
              </div>

              <h2 style={{ fontSize: nameFontSize, fontWeight: 700, color: "#111827", margin: "12px 0 2px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                {pro.name}
                <svg width="18" height="18" viewBox="0 0 20 20" fill="#3b82f6">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </h2>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>{pro.service}</p>

              {/* Stats row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <svg width="22" height="22" viewBox="0 0 52 52">
                    <circle cx={26} cy={26} r={20} fill="none" stroke="#e5e7eb" strokeWidth={5}/>
                    <circle cx={26} cy={26} r={20} fill="none" stroke={tokens.color.accent} strokeWidth={5}
                      strokeDasharray={`${(pro.successScore/100)*125.6} 125.6`}
                      strokeDashoffset={31.4} strokeLinecap="round"/>
                  </svg>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{pro.successScore}%</span>
                </div>
                {pro.badge && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" }}>⭐ {pro.badge}</span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginBottom: 6 }}>
                <Stars rating={pro.rating} size={13} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{pro.rating}</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>({pro.reviews})</span>
              </div>
              <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>📍 {pro.city}</p>
            </div>

            {/* Stats grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: tokens.color.neutral200, border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.md, overflow: "hidden", marginBottom: 20 }}>
              {[
                { label: "Total Jobs", value: pro.reviews * 2 },
                { label: "Earnings", value: pro.earnings },
                { label: "Rating", value: `${pro.rating}★` },
                { label: "Response", value: "< 1 hr" },
              ].map((s, i) => (
                <div key={i} style={{ background: tokens.color.white, padding: "14px 10px", textAlign: "center" }}>
                  <p style={{ fontSize: 17, fontWeight: 800, color: "#111827", margin: "0 0 3px", fontFamily: tokens.font.family.sans }}>{s.value}</p>
                  <p style={{ fontSize: 10, color: "#9ca3af", margin: 0, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <button 
              onClick={handleMessage}
              style={{ width: "100%", background: tokens.color.accent, color: tokens.color.white, border: "none", borderRadius: tokens.radius.md, padding: "13px", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10, transition: `background ${tokens.motion.base} ${tokens.motion.ease}`, fontFamily: tokens.font.family.sans, minHeight: isMobile ? tokens.spacing[44] : "auto" }}
              onMouseEnter={e => e.currentTarget.style.background = tokens.color.primaryDark}
              onMouseLeave={e => e.currentTarget.style.background = tokens.color.accent}>
              Message
            </button>
            <button
              onClick={handleBookNow}
              style={{ width: "100%", background: tokens.color.white, color: tokens.color.accent, border: `1.5px solid ${tokens.color.accent}`, borderRadius: tokens.radius.md, padding: "11px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: `background ${tokens.motion.base} ${tokens.motion.ease}`, fontFamily: tokens.font.family.sans, minHeight: isMobile ? tokens.spacing[44] : "auto" }}
              onMouseEnter={e => e.currentTarget.style.background = tokens.color.accentLight}
              onMouseLeave={e => e.currentTarget.style.background = tokens.color.white}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
              Book now
            </button>
            <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", marginTop: 8, fontFamily: tokens.font.family.sans }}>
              Ksh {pro.price.toLocaleString()} per job
            </p>
          </div>

          {/* RIGHT: sticky tabs + scrollable content */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {/* Sticky tab bar */}
            <div style={{ padding: isMobile ? "0 16px" : "0 32px", borderBottom: `1px solid ${tokens.color.neutral200}`, background: tokens.color.white, flexShrink: 0, display: "flex", alignItems: "center", height: 52, overflowX: "auto", scrollbarWidth: "none" }}>
              <div style={{ display: "flex", gap: 4, background: tokens.color.neutral100, padding: 4, borderRadius: 30, width: "fit-content" }}>
                {TABS.map(tab => (
                  <button key={tab} onClick={() => scrollToSection(tab)}
                    style={{
                      padding: "7px 16px", borderRadius: 24,
                      background: activeTab === tab ? "#111827" : "transparent",
                      color: activeTab === tab ? tokens.color.white : "#6b7280",
                      border: "none", cursor: "pointer",
                      fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
                      fontFamily: tokens.font.family.sans,
                      transition: `all ${tokens.motion.base} ${tokens.motion.ease}`,
                      whiteSpace: "nowrap",
                      minHeight: isMobile ? tokens.spacing[44] : "auto",
                    }}>
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable content — ALL sections stacked */}
            <div ref={contentRef} style={{ flex: 1, overflowY: "auto", padding: isMobile ? "16px 16px 40px" : "20px 32px 60px" }}>

              {/* ── ABOUT ── */}
              <div ref={el => sectionRefs.current["About"] = el} style={{ marginBottom: 48 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 8 : 0 }}>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: 0, fontFamily: tokens.font.family.sans }}>
                    About {pro.name.split(" ")[0]}
                  </h3>
                  <div style={{ textAlign: isMobile ? "left" : "right" }}>
                    <p style={{ fontSize: 18, fontWeight: 800, color: "#111827", margin: 0, fontFamily: tokens.font.family.sans }}>Ksh {pro.price.toLocaleString()}/job</p>
                    <p style={{ fontSize: 12, color: "#9ca3af", margin: 0, fontFamily: tokens.font.family.sans }}>Rate</p>
                  </div>
                </div>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, marginBottom: 20, fontFamily: tokens.font.family.sans }}>
                  {pro.bio}
                </p>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: "#111827", margin: "0 0 12px", fontFamily: tokens.font.family.sans }}>What I Do Well:</h4>
                {pro.skills.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 18, height: 18, borderRadius: 3, background: tokens.color.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="10" height="10" viewBox="0 0 20 20" fill="white"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    </div>
                    <span style={{ fontSize: 14, color: "#374151", fontFamily: tokens.font.family.sans }}>{s}</span>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `1px solid ${tokens.color.neutral200}`, marginBottom: 48 }} />

              {/* ── CLIENT FEEDBACK ── */}
              <div ref={el => sectionRefs.current["Client feedback"] = el} style={{ marginBottom: 48 }}>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 20px", fontFamily: tokens.font.family.sans }}>
                  Client feedback ({pro.reviews})
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                  {[
                    { name: "James O.", date: "Jan 2025", rating: 5, text: "Absolutely incredible — thorough, professional and arrived on time. Would hire again without hesitation." },
                    { name: "Sarah W.", date: "Dec 2024", rating: 5, text: "Got my full deposit back thanks to this pro! Landlord was genuinely impressed. Highly recommended." },
                    { name: "David M.", date: "Dec 2024", rating: 5, text: "Very professional, worked quietly and efficiently. My place looked spotless when they left." },
                    { name: "Grace A.", date: "Nov 2024", rating: 4, text: "Great work overall. Took a little longer than expected but the quality made up for it completely." },
                  ].map((r, i) => (
                    <div key={i} style={{ border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.md, padding: "16px 18px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: "#111827", margin: 0, fontFamily: tokens.font.family.sans }}>{r.name}</p>
                          <p style={{ fontSize: 12, color: "#9ca3af", margin: 0, fontFamily: tokens.font.family.sans }}>{r.date}</p>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Stars rating={r.rating} size={12} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{r.rating}.0</span>
                        </div>
                      </div>
                      <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, margin: "0 0 10px", fontFamily: tokens.font.family.sans }}>"{r.text}"</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#6b7280" }}>{r.name[0]}</div>
                        <span style={{ fontSize: 12, color: "#6b7280", fontFamily: tokens.font.family.sans }}>{r.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${tokens.color.neutral200}`, marginBottom: 48 }} />

              {/* ── WORK HISTORY ── */}
              <div ref={el => sectionRefs.current["Work history"] = el} style={{ marginBottom: 48 }}>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 20px", fontFamily: tokens.font.family.sans }}>
                  Work history on JiraniApp
                </h3>
                {[
                  { title: `${pro.service} — 3-bedroom home, Nairobi`, date: "Jan 2025", rating: 5, earnings: `Ksh ${pro.price.toLocaleString()}` },
                  { title: `${pro.service} — Office space, CBD`, date: "Dec 2024", rating: 5, earnings: `Ksh ${(pro.price * 2).toLocaleString()}` },
                  { title: `${pro.service} — Studio apartment, Westlands`, date: "Dec 2024", rating: 4, earnings: `Ksh ${pro.price.toLocaleString()}` },
                  { title: `${pro.service} — Family home, Karen`, date: "Nov 2024", rating: 5, earnings: `Ksh ${(pro.price * 1.5).toLocaleString()}` },
                ].map((job, i, arr) => (
                  <div key={i} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${tokens.color.neutral100}` : "none", paddingBottom: 18, marginBottom: 18 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 4 : 0 }}>
                      <h4 style={{ fontSize: 14, fontWeight: 600, color: "#111827", margin: 0, fontFamily: tokens.font.family.sans }}>{job.title}</h4>
                      <span style={{ fontSize: 13, color: "#6b7280", flexShrink: 0, fontFamily: tokens.font.family.sans }}>{job.earnings}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Stars rating={job.rating} size={12} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{job.rating}.0</span>
                      <span style={{ color: "#d1d5db" }}>·</span>
                      <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: tokens.font.family.sans }}>{job.date}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `1px solid ${tokens.color.neutral200}`, marginBottom: 48 }} />

              {/* ── PORTFOLIO ── */}
              <div ref={el => sectionRefs.current["Portfolio"] = el} style={{ marginBottom: 48 }}>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 20px", fontFamily: tokens.font.family.sans }}>
                  Portfolio ({pro.skills.length})
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : (isTablet ? "repeat(2, 1fr)" : "repeat(3, 1fr)"), gap: 16 }}>
                  {pro.skills.slice(0, 3).map((skill, i) => {
                    const hues = [140, 200, 260];
                    return (
                      <div key={i} style={{ borderRadius: tokens.radius.md, overflow: "hidden", border: `1px solid ${tokens.color.neutral200}`, cursor: "pointer", transition: `box-shadow ${tokens.motion.base} ${tokens.motion.ease}` }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 16px ${tokens.color.shadowMd}`}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                        <div style={{ height: 120, background: `hsl(${hues[i]}, 40%, 55%)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.9)", textAlign: "center", padding: "0 12px", fontFamily: tokens.font.family.sans }}>{skill}</span>
                        </div>
                        <div style={{ padding: "10px 12px" }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: "#111827", margin: "0 0 6px", fontFamily: tokens.font.family.sans }}>{pro.service} — {skill}</p>
                          <span style={{ fontSize: 11, color: tokens.color.accent, background: tokens.color.accentLight, border: "1px solid #bbf7d0", borderRadius: 20, padding: "2px 10px" }}>{skill}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${tokens.color.neutral200}`, marginBottom: 48 }} />

              {/* ── SKILLS ── */}
              <div ref={el => sectionRefs.current["Skills"] = el}>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 16px", fontFamily: tokens.font.family.sans }}>Skills</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32 }}>
                  {pro.skills.map((s, i) => (
                    <span key={i} style={{ fontSize: 13, color: "#374151", border: `1px solid ${tokens.color.neutral300}`, borderRadius: 20, padding: "5px 16px", fontFamily: tokens.font.family.sans }}>{s}</span>
                  ))}
                </div>

                <h4 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 14px", fontFamily: tokens.font.family.sans }}>Browse similar JiraniPros</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {pro.skills.slice(0, 4).map((s, i) => (
                    <span key={i} onClick={() => handleSimilarProClick(s)} style={{ fontSize: 14, color: tokens.color.accent, cursor: "pointer", fontWeight: 500, fontFamily: tokens.font.family.sans, minHeight: isMobile ? tokens.spacing[44] : "auto", display: "inline-block", padding: isMobile ? "4px 0" : 0 }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                      onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
                      {s} Specialists →
                    </span>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}